import React, { createContext, useCallback, useContext } from 'react';
import type { DokladyDokladDto } from '@gov-nx/api';
import {
	sendComplaintQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { useMessageEvents } from '@gov-nx/core/events';
import { useProcessControl } from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import type { PageCode } from '../../definitions/codes';
import { FormInstance, prepareSubmitData } from './FormDefinitions';
import type {
	DocumentsComplaintContext,
	DocumentsComplaintProcessControl,
	FormData,
} from './context.types';

const DokumentyReklamaceContext =
	createContext<Nullable<DocumentsComplaintContext>>(null);

interface DocumentsComplaintContextProviderProps {
	children: React.ReactNode;
	code: PageCode;
	document: DokladyDokladDto;
	onComplaintSubmit?: () => void;
}

export function DokumentyReklamaceContextProvider({
	children,
	code,
	document,
	onComplaintSubmit,
}: DocumentsComplaintContextProviderProps) {
	const { setControls, controls } =
		useProcessControl<DocumentsComplaintProcessControl>({
			initialDataError: null,
			initialCountryError: null,
		});

	const { toastMessageSuccess } = useMessageEvents();
	const { t } = useLocale(code);

	const formDefinition = FormInstance({ code, document });
	const { axiosInstance } = useAxiosInstance();
	const submitMutation = usePoMutation({
		mutationFn: async (data: FormData) => {
			const prepared = prepareSubmitData(document)(data);
			return sendComplaintQuery(axiosInstance, prepared);
		},

		onError: (error) => {
			setControls({ processError: error, processLoading: false });
		},
		onSuccess: async () => {
			toastMessageSuccess(t('formular.zprava.odeslano'));
			setControls({ processLoading: false });

			formDefinition.formReset();
			onComplaintSubmit && onComplaintSubmit();
		},
	});

	const handleSubmit = useCallback(async () => {
		setControls({ processError: null, processLoading: true });
		submitMutation.mutate(formDefinition.formMethods.getValues());
	}, [formDefinition.formMethods, submitMutation, setControls]);

	const onSubmit = formDefinition.formMethods.handleSubmit(handleSubmit);

	return (
		<DokumentyReklamaceContext.Provider
			value={{
				controls,
				formDefinition,
				document,
				onSubmit,
				disableSubmitButton: submitMutation.isSuccess,
			}}>
			{children}
		</DokumentyReklamaceContext.Provider>
	);
}

export const useDokumentyReklamaceContextInstance =
	(): DocumentsComplaintContext =>
		useContext(DokumentyReklamaceContext) as DocumentsComplaintContext;
